import { Component, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import type {
  CalculatorBootstrap,
  ConfigSource,
  ControlAnimation,
  ControlShape,
  ControlSize,
  ControlStyle,
  FieldValues,
} from './types';
import { CalculatorApp } from './chrome/CalculatorApp';

/**
 * Mount contract (docs/calculator-engine-architecture.md §9.4). Every
 * calculator entry file calls autoMount(id, config) identically — this is
 * the one piece of literally-identical code across all four calculators.
 */

function ErrorPanel({ message, editMode }: { message: string; editMode: boolean }) {
  return (
    <div className="hsab-calc__error-panel" role="alert">
      <p>This calculator couldn&apos;t load.</p>
      {editMode && <pre>{message}</pre>}
    </div>
  );
}

interface BoundaryProps {
  children: ReactNode;
  editMode: boolean;
}
interface BoundaryState {
  error: Error | null;
}

class CalculatorErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  render() {
    if (this.state.error) {
      return <ErrorPanel message={this.state.error.message} editMode={this.props.editMode} />;
    }
    return this.props.children;
  }
}

function isValidBootstrap(data: unknown): data is CalculatorBootstrap {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  if (typeof d.calculatorId !== 'string') return false;
  if (!d.limits || typeof d.limits !== 'object') return false;
  const limits = d.limits as Record<string, unknown>;
  if (!limits.current || typeof limits.current !== 'object') return false;
  if (!d.options || typeof d.options !== 'object') return false;
  // controlStyle is deliberately NOT required here (docs/calculator-control-style.md
  // §4.3) -- a page HTML response cached by Fastly before this feature shipped
  // has no controlStyle at all, and that must still render today's look, not
  // throw. resolveControlStyle() below re-defaults it instead.
  return true;
}

/**
 * Re-defaults an absent, malformed, or (a future) unrecognized controlStyle
 * to today's rendering rather than trusting the blob — the runtime-validation
 * half of the same guarantee calculator-app.ftl's own guard provides on the
 * JCR side (docs/calculator-control-style.md §4.3): the FTL guard protects
 * against bad content, this one against a stale or hand-edited blob. Neither
 * is redundant with the other.
 */
function resolveControlStyle(raw: unknown): ControlStyle {
  const shapes: ControlShape[] = ['default', 'square', 'round'];
  const sizes: ControlSize[] = ['large', 'small'];
  const animations: ControlAnimation[] = ['none', 'fade', 'slide'];
  const cs = (raw ?? {}) as Partial<ControlStyle>;
  return {
    shape: shapes.includes(cs.shape as ControlShape) ? (cs.shape as ControlShape) : 'default',
    size: sizes.includes(cs.size as ControlSize) ? (cs.size as ControlSize) : 'large',
    animation: animations.includes(cs.animation as ControlAnimation) ? (cs.animation as ControlAnimation) : 'fade',
    countUp: cs.countUp === true,
  };
}

/** Parses and validates the JSON blob, then mounts (or renders an error panel). */
export function mountCalculator<TValues extends FieldValues, TResult>(
  mountEl: HTMLElement,
  configSource: ConfigSource<TValues, TResult>,
  dataEl: HTMLElement,
): void {
  let bootstrap: CalculatorBootstrap | null = null;
  let parseError: string | null = null;

  try {
    const parsed: unknown = JSON.parse(dataEl.textContent ?? '');
    if (isValidBootstrap(parsed)) {
      bootstrap = parsed;
    } else {
      parseError = 'Calculator data blob is missing required fields.';
    }
  } catch (e) {
    parseError = e instanceof Error ? e.message : 'Could not parse calculator data.';
  }

  const editMode = bootstrap?.options.editMode ?? false;
  const root = createRoot(mountEl);

  if (!bootstrap) {
    root.render(<ErrorPanel message={parseError ?? 'Unknown error'} editMode={editMode} />);
    return;
  }

  const config = typeof configSource === 'function' ? configSource(bootstrap.options) : configSource;

  root.render(
    <CalculatorErrorBoundary editMode={editMode}>
      <CalculatorApp
        config={config}
        limits={bootstrap.limits}
        options={bootstrap.options}
        controlStyle={resolveControlStyle(bootstrap.controlStyle)}
      />
    </CalculatorErrorBoundary>,
  );
}

/**
 * Finds every mount point for this calculator on the page and mounts into
 * each with its paired JSON blob, so multiple instances of the same
 * calculator on one page work independently (unlike today's global
 * CalculatorsData var, which two instances would collide on).
 */
export function autoMount<TValues extends FieldValues, TResult>(
  calculatorId: string,
  config: ConfigSource<TValues, TResult>,
): void {
  const mounts = document.querySelectorAll<HTMLElement>(`.hsab-calc-mount[data-calculator="${calculatorId}"]`);

  mounts.forEach((mountEl) => {
    const dataId = mountEl.getAttribute('data-blob-id');
    const dataEl = dataId ? document.getElementById(dataId) : null;

    if (!dataEl) {
      const root = createRoot(mountEl);
      root.render(<ErrorPanel message="No calculator data blob found for this mount point." editMode={false} />);
      return;
    }

    mountCalculator(mountEl, config, dataEl);
  });
}
